import { Rocket, Sprout } from "lucide-react";
import { agentMap, modeEmphasis, toneSurface } from "@/lib/mockData";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";

const TILES = [
  {
    value: "bootstrap",
    Icon: Sprout,
    title: "Bootstrapping",
    blurb:
      "Optimize for capital efficiency, organic growth, profitable from day one.",
  },
  {
    value: "funding",
    Icon: Rocket,
    title: "Funding",
    blurb:
      "Optimize for growth velocity, hiring, fundraising, and scaling fast.",
  },
];

export default function OperatingModeCard() {
  const mode = useStore((s) => s.mode);
  const setMode = useStore((s) => s.setMode);
  const emphasis = modeEmphasis[mode];

  return (
    <section
      data-testid="operating-mode-card"
      className="card-soft rounded-[28px] p-6 md:p-7"
    >
      <header className="mb-5">
        <h2 className="text-[18px] font-extrabold tracking-tight text-zinc-900">
          Operating Mode
        </h2>
        <p className="mt-1 text-[13px] text-zinc-500">
          Switch the boardroom's strategic emphasis.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {TILES.map(({ value, Icon, title, blurb }) => {
          const active = mode === value;
          return (
            <button
              key={value}
              type="button"
              data-testid={`mode-tile-${value}`}
              data-active={active ? "true" : "false"}
              onClick={() => setMode(value)}
              className={cn(
                "group flex flex-col gap-3 rounded-2xl border p-5 text-left transition-all",
                active
                  ? "border-2 border-zinc-900 bg-white shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04),0_18px_36px_-20px_rgba(15,15,25,0.25)]"
                  : "border border-black/[0.06] bg-white/60 hover:bg-white",
              )}
            >
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-2xl",
                    active
                      ? "bg-zinc-900 text-white"
                      : "bg-zinc-100 text-zinc-700",
                  )}
                >
                  <Icon className="h-5 w-5" strokeWidth={2.2} />
                </span>
                {active && (
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-zinc-900 text-[10px] font-bold text-white">
                    ✓
                  </span>
                )}
              </div>
              <div className="text-[18px] font-extrabold tracking-tight text-zinc-900">
                {title}
              </div>
              <p className="text-[13px] leading-relaxed text-zinc-600">
                {blurb}
              </p>
            </button>
          );
        })}
      </div>

      <div
        data-testid="mode-emphasis-chip"
        className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl bg-zinc-50 px-4 py-3"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
          Boardroom emphasis
        </span>
        <div className="flex items-center gap-1.5">
          {emphasis.agents.map((id) => {
            const a = agentMap[id];
            const tone = toneSurface[a?.tone] ?? toneSurface.neutral;
            const isDark = a?.tone === "dark";
            return (
              <span
                key={id}
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-extrabold",
                  isDark
                    ? "bg-[#0B0B0E] text-white"
                    : cn(tone.bg, "text-zinc-900"),
                )}
                style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}
                title={`${a?.name} · ${a?.role}`}
              >
                {a?.name?.[0]}
              </span>
            );
          })}
        </div>
        <span className="text-[12px] text-zinc-600">{emphasis.label}</span>
      </div>
    </section>
  );
}
